import React, { useState, useEffect } from "react";
import Modal from "@/components/atoms/Modal";
import FormInput from "@/components/atoms/FormInput";
import TabNavigation from "@/components/atoms/TabNavigation";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import useAppStore from "@/stores/useAppStore";
import { type UpdateProfileDto, type ChangePasswordDto } from "@/types/dto";
import { ChangeUserPassword, UpdateUserProfile } from "@/apis/User";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  [key: string]: string;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const user = useAppStore((s) => s.user);
  const setUser = useAppStore((s) => s.setUser);

  const [activeTab, setActiveTab] = useState("profile");

  // Profile form state
  const [profileData, setProfileData] = useState<UpdateProfileDto>({
    name: user?.name || "",
  });
  const [profileErrors, setProfileErrors] = useState<FormErrors>({});
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password form state
  const [passwordData, setPasswordData] = useState<ChangePasswordDto>({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<FormErrors>({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const tabs = [
    {
      id: "profile",
      label: "Profile",
    },
    {
      id: "password",
      label: "Password",
    },
  ];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setProfileData({ name: user.name });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      setProfileErrors({});
      setPasswordErrors({});
      setActiveTab("profile");
    }
  }, [isOpen, user]);

  const validateProfileForm = (): boolean => {
    const errors: FormErrors = {};

    if (!profileData.name.trim()) {
      errors.name = "Name is required";
    } else if (profileData.name.length > 100) {
      errors.name = "Name is too long";
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = (): boolean => {
    const errors: FormErrors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters long";
    }

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      errors.confirmNewPassword = "New passwords don't match";
    }

    if (
      passwordData.currentPassword === passwordData.newPassword &&
      passwordData.newPassword
    ) {
      errors.newPassword =
        "New password must be different from current password";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    setIsUpdatingProfile(true);

    try {
      UpdateUserProfile(profileData).subscribe({
        next: () => {
          if (user) {
            const updatedUser = { ...user, name: profileData.name };
            setUser(updatedUser);
          }

          toast.success("Profile updated successfully!");
        },
        error: (error) => {
          toast.error(error.message || "Failed to update profile");
        },
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      ChangeUserPassword(passwordData).subscribe({
        next: () => {
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          });
          toast.success("Password changed successfully!");
        },
        error: (error) => {
          toast.error(error.message || "Failed to change password");
        },
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleClose = () => {
    if (!isUpdatingProfile && !isChangingPassword) {
      onClose();
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Profile Settings"
      size="md"
    >
      <div className="space-y-6">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        {activeTab === "profile" && (
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <div className="space-y-4">
              <FormInput
                id="name"
                label="Full Name"
                value={profileData.name}
                onChange={(value) =>
                  setProfileData((prev) => ({ ...prev, name: value }))
                }
                placeholder="Enter your full name"
                error={profileErrors.name}
                disabled={isUpdatingProfile}
                required
              />

              <FormInput
                id="email"
                label="Email Address"
                type="email"
                value={user.email}
                onChange={() => {}} // Email is read-only
                disabled={true}
                placeholder="Email cannot be changed"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUpdatingProfile}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUpdatingProfile || profileData.name === user.name}
              >
                {isUpdatingProfile && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isUpdatingProfile ? "Updating..." : "Update Profile"}
              </Button>
            </div>
          </form>
        )}

        {activeTab === "password" && (
          <form onSubmit={handleChangePassword} className="space-y-6">
            <div className="space-y-4">
              <FormInput
                id="currentPassword"
                label="Current Password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(value) =>
                  setPasswordData((prev) => ({
                    ...prev,
                    currentPassword: value,
                  }))
                }
                placeholder="Enter your current password"
                error={passwordErrors.currentPassword}
                disabled={isChangingPassword}
                required
              />

              <FormInput
                id="newPassword"
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(value) =>
                  setPasswordData((prev) => ({ ...prev, newPassword: value }))
                }
                placeholder="Enter your new password"
                error={passwordErrors.newPassword}
                disabled={isChangingPassword}
                required
              />

              <FormInput
                id="confirmNewPassword"
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmNewPassword}
                onChange={(value) =>
                  setPasswordData((prev) => ({
                    ...prev,
                    confirmNewPassword: value,
                  }))
                }
                placeholder="Confirm your new password"
                error={passwordErrors.confirmNewPassword}
                disabled={isChangingPassword}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isChangingPassword}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isChangingPassword ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default ProfileModal;
